import { useState } from 'react'
import { Box, Link, Stack, Typography } from '@mui/material'
import { ArrowUpRightIcon, RefreshCwIcon, SparklesIcon } from 'lucide-react'
import {
  CONTENT_FEED_TRIGGER_LABELS,
  RECOMMENDATION_CONSTRAINT_LABELS,
  type ContentFeedRun,
} from '@repo/types'
import { Alert, Button, Modal } from '@repo/ui'

import { Term } from 'components/feedback/Term'
import { useSnackbar } from 'contexts'
import {
  useContentFeedRuns,
  useContentUnmapped,
  useFeedContentIdeas,
  useRecommendationBatches,
  useSuggestRecommendations,
} from 'hooks/api'

import { MANAGE_IDEAS_TIP } from './contentIdeaModel'


const when = (iso: string) => {
  const d = new Date(iso)
  return `${d.toISOString().slice(0, 10)} ${d.toISOString().slice(11, 16)} UTC`
}

/** "12 items seen · 3 ideas created · 1 merged · 1 guest suggested · 8 unmapped · 40 backfill (reference only)" */
export const runSummary = (r: ContentFeedRun): string =>
  [
    `${r.items_seen} item${r.items_seen === 1 ? '' : 's'} seen`,
    `${r.ideas_created} idea${r.ideas_created === 1 ? '' : 's'} created`,
    `${r.ideas_merged} merged`,
    `${r.guests_suggested} guest${r.guests_suggested === 1 ? '' : 's'} suggested`,
    `${r.unmapped_items} unmapped`,
    // VRG-741 flood guard: backlog held as reference only (shown only when there is some).
    ...(r.backfill_items ? [`${r.backfill_items} backfill (reference only)`] : []),
  ].join(' · ')

/**
 * VRG-741 — above the Ideas table: the latest feed run's summary (items seen, ideas created or
 * merged, guests suggested, unmapped items), "Run feed" (both feeds, by hand), "Suggest 5 more"
 * (recommendation cards for the top ideas), and the latest batch's relaxed variety rules. The two
 * lists behind links: every recent run, and the unmapped items with their candidate keywords
 * (review only; nothing is added to the keyword master from here).
 */
export const IdeasFeedBar = ({ canManage, onOpenIdea }: { canManage: boolean; onOpenIdea: (id: string) => void }) => {
  const { open: notify } = useSnackbar()
  const { data: runs } = useContentFeedRuns()
  const { data: batches } = useRecommendationBatches()
  const feed = useFeedContentIdeas()
  const suggest = useSuggestRecommendations()
  const [showRuns, setShowRuns] = useState(false)
  const [showUnmapped, setShowUnmapped] = useState(false)
  const { data: unmapped, isLoading: unmappedLoading } = useContentUnmapped(showUnmapped)
  const last = runs?.[0]
  const batch = batches?.[0]
  const graphics = (batch?.report as { graphics?: { varied?: boolean; note?: string | null } } | null)?.graphics

  const fail = (e: unknown) => notify({ type: 'error', text: (e as Error).message })

  return (
    <Stack spacing={3} sx={{ mb: 4 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} justifyContent="space-between" alignItems={{ md: 'center' }}>
        <Typography variant="body3" color="text.secondary" component="div">
          {last ? (
            <>
              Last <Term id="content-corpus-feed">feed run</Term> {when(last.ran_at)} ({CONTENT_FEED_TRIGGER_LABELS[last.trigger]}): {runSummary(last)}
              {last.seo ? ` · SEO ${last.seo.created} created` : ''}
              {last.error ? ` · failed: ${last.error}` : ''}.{' '}
              <Link component="button" type="button" underline="hover" onClick={() => setShowRuns(true)} sx={{ font: 'inherit' }}>
                All runs
              </Link>
              {' · '}
              <Link component="button" type="button" underline="hover" onClick={() => setShowUnmapped(true)} sx={{ font: 'inherit' }}>
                Unmapped items
              </Link>
            </>
          ) : (
            'No feed run logged yet. Ideas arrive after each SEO recompute and each source poll that finds new items.'
          )}
        </Typography>
        <Stack direction="row" spacing={3} sx={{ flexShrink: 0 }}>
          <Button
            variant="secondary"
            startIcon={<RefreshCwIcon strokeWidth={1} size={18} />}
            disabled={!canManage || feed.isPending}
            disabledReason={canManage ? undefined : MANAGE_IDEAS_TIP}
            loading={feed.isPending}
            onClick={() =>
              feed.mutate(undefined, {
                onSuccess: (r) => notify({ type: 'success', text: `Feed run: ${runSummary(r)}.` }),
                onError: fail,
              })
            }
          >
            Run feed
          </Button>
          <Button
            variant="secondary"
            startIcon={<SparklesIcon strokeWidth={1} size={18} />}
            disabled={!canManage || suggest.isPending}
            disabledReason={canManage ? undefined : MANAGE_IDEAS_TIP}
            loading={suggest.isPending}
            onClick={() =>
              suggest.mutate(
                { count: 5 },
                {
                  onSuccess: (r) => {
                    const ideas = (r as { ideas: { id: string }[] }).ideas
                    notify({
                      type: 'success',
                      text: ideas.length
                        ? `${ideas.length} recommendation${ideas.length === 1 ? '' : 's'} ready to approve or reject.`
                        : 'No ideas left at Idea without a recommendation.',
                    })
                    if (ideas[0]) onOpenIdea(ideas[0].id)
                  },
                  onError: fail,
                },
              )
            }
          >
            Suggest 5 more
          </Button>
        </Stack>
      </Stack>

      {batch && (batch.relaxed.length || graphics?.note) ? (
        <Alert
          severity="info"
          description={
            <>
              Latest batch ({batch.idea_ids.length} cards, {batch.created_at.slice(0, 10)}) relaxed these <Term id="content-variety-rules">variety rules</Term>:{' '}
              {batch.relaxed.length
                ? batch.relaxed.map((r) => `${RECOMMENDATION_CONSTRAINT_LABELS[r.constraint]} (${r.reason})`).join('; ')
                : 'nothing'}
              .{graphics?.note ? ` ${graphics.note}` : ''}
            </>
          }
        />
      ) : null}

      <Modal open={showRuns} onClose={() => setShowRuns(false)} title="Feed runs" description="The last 20 runs of the idea feeds. Database only: no model or vendor calls.">
        <Stack spacing={2}>
          {(runs ?? []).map((r) => (
            <Box key={r.id}>
              <Typography variant="body3" sx={{ fontWeight: 600 }}>
                {when(r.ran_at)} · {CONTENT_FEED_TRIGGER_LABELS[r.trigger]}
                {r.actor ? ` · ${r.actor}` : ''}
              </Typography>
              <Typography variant="body4" color="text.secondary" component="div">
                {runSummary(r)}
                {r.seo ? ` · SEO: ${r.seo.created} created, ${r.seo.terms_added} terms added` : ''}
                {Object.keys(r.skipped).length
                  ? ` · skipped: ${Object.entries(r.skipped)
                      .map(([k, v]) => `${k.replace(/_/g, ' ')} ${v}`)
                      .join(', ')}`
                  : ''}
                {r.error ? ` · failed: ${r.error}` : ''}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Modal>

      <Modal
        open={showUnmapped}
        onClose={() => setShowUnmapped(false)}
        title="Unmapped items"
        description="Corpus items that matched no tracked SEO term, so they made no idea. The phrases are candidate keywords for review; nothing is added to the keyword master from here."
      >
        <Stack spacing={2}>
          {unmappedLoading ? <Typography variant="body3">Loading…</Typography> : null}
          {!unmappedLoading && !(unmapped ?? []).length ? (
            <Typography variant="body3" color="text.secondary">
              Nothing unmapped.
            </Typography>
          ) : null}
          {(unmapped ?? []).map((u) => (
            <Box key={u.market_item_id}>
              <Typography variant="body3" noWrap title={u.title ?? u.url}>
                <Link
                  href={u.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  color="inherit"
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                >
                  {u.title ?? u.url}
                  <ArrowUpRightIcon strokeWidth={1} size={14} style={{ opacity: 0.6 }} />
                </Link>
              </Typography>
              <Typography variant="body4" color="text.secondary" component="div">
                {[u.source_name ?? u.publication, u.phrases.length ? `candidate keywords: ${u.phrases.join(', ')}` : 'no candidate phrase']
                  .filter(Boolean)
                  .join(' · ')}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Modal>
    </Stack>
  )
}
